import { Converter } from 'opencc-js/t2cn';
import type { Locale, NpcId } from './enums';
import type { LocalizedText } from './common';

import { CAST_NAMES, chineseNames } from './names';

// Hong Kong Traditional → Mainland Simplified, including phrase differences (e.g. 軟件/软件 wording).
const hk2cn = Converter({ from: 'hk', to: 'cn' });
/** 著 as the aspect particle is 着 in Mainland usage; the words below keep 著. */
const KEEP_ZHU = /著(?=名|作|称|述|者|录|书|重)|(?<=显|土|卓|原|巨|昭|名|论|编|拙|遗)著/g;
function mainland(text: string): string {
  const kept: number[] = [];
  for (const m of text.matchAll(KEEP_ZHU)) kept.push(m.index!);
  let out = '';
  for (let i = 0; i < text.length; i++) out += text[i] === '著' && !kept.includes(i) ? '着' : text[i];
  // Mainland wording OpenCC leaves in Hong Kong form, then mainland quotation marks.
  out = out.replace(/私隐/g, '隐私');
  return out.replace(/「/g, '“').replace(/」/g, '”').replace(/『/g, '‘').replace(/』/g, '’');
}
const cache = new Map<string, string>();

/** Simplified Chinese for a Traditional string. Memoised: the same lines are converted once. */
export function toSimplified(text: string): string {
  const hit = cache.get(text);
  if (hit !== undefined) return hit;
  const out: string = mainland(hk2cn(text) ?? text);
  if (cache.size > 20_000) cache.clear();
  cache.set(text, out);
  return out;
}

/**
 * A space between Chinese and Latin letters or digits, as the authored copy writes it
 * (「在墨點咖啡幫忙」): for Chinese lines built from names, e.g. `去${place}找${name}`.
 */
export function spaceLatin(text: string): string {
  return text
    .replace(/([\u3400-\u9fff])([A-Za-z0-9])/g, '$1 $2')
    .replace(/([A-Za-z0-9é.])([\u3400-\u9fff])/g, '$1 $2');
}

/** Text in any locale from its authored pair. */
export function localize(locale: Locale, zh: string, en: string): string {
  return locale === 'en' ? en : locale === 'zh-CN' ? toSimplified(chineseNames(zh)) : chineseNames(zh);
}

/** Same, for a LocalizedText value. */
export const trText = (text: LocalizedText, locale: Locale) => localize(locale, text['zh-HK'], text.en);

/** Every string in a value mapped by `f` (deep through arrays and plain objects; everything else kept). */
export function mapStrings<T>(value: T, f: (s: string) => string): T {
  if (typeof value === 'string') return f(value) as T;
  if (Array.isArray(value)) return value.map((x: unknown) => mapStrings(x, f)) as T;
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([k, x]) => [k, mapStrings(x, f)])) as T;
  return value;
}

/** A cast member's name in this locale (Chinese in the Chinese UIs); anyone outside the cast gets their id capitalised. */
export function castName(id: string, locale: Locale): string {
  const n = CAST_NAMES[id as NpcId];
  if (!n) return id.slice(0, 1).toUpperCase() + id.slice(1);
  return localize(locale, n.zh, n.en);
}
